import { normalizeTableRows } from "./normalizeTableRows";

/** Default SQL table page size for chat "Load more". */
export const TABLE_PAGE_SIZE = 100;

/** Backend execute-sql accepts limit only between 1 and this value. */
export const EXECUTE_SQL_MAX_LIMIT = 1000;

function readNonNegativeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function readPositiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function readBoolean(value) {
  if (value === true || value === false) return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

function pickPaginationHasMore(paginationInfo = {}, dataSummary = {}) {
  return readBoolean(
    paginationInfo.has_more ??
      paginationInfo.hasMore ??
      paginationInfo.has_next ??
      paginationInfo.hasNext ??
      dataSummary.has_more ??
      dataSummary.hasMore
  );
}

/**
 * Collect pagination + data_summary from query API payloads (top-level or nested).
 */
export function extractTablePaginationSources(payload = {}) {
  const metadata = payload.metadata ?? {};
  const vizData = payload.visualization_data ?? metadata.visualization_data ?? {};

  const pagination = {
    ...(vizData.pagination ?? {}),
    ...(metadata.pagination ?? {}),
    ...(payload.pagination ?? {}),
  };

  const dataSummary = {
    ...(vizData.data_summary ?? vizData.dataSummary ?? {}),
    ...(metadata.data_summary ?? metadata.dataSummary ?? {}),
    ...(payload.data_summary ?? payload.dataSummary ?? {}),
  };

  return { pagination, dataSummary };
}

/** All SQL rows returned with a query response (top-level or nested in visualization_data). */
export function extractRawResultsFromPayload(payload = {}) {
  const metadata = payload.metadata ?? {};
  const vizData = payload.visualization_data ?? metadata.visualization_data ?? {};
  const rows = vizData.raw_results ?? payload.raw_results;
  return normalizeTableRows(Array.isArray(rows) ? rows : [], null);
}

/**
 * Resolve the authoritative total row count for a SQL table.
 */
export function resolveTotalRowCount(
  dataSummary = {},
  paginationInfo = {},
  fallback = null,
  loadedCount = 0,
  fullResultCount = null
) {
  const loaded = Number(loadedCount) || 0;
  const hasMore = pickPaginationHasMore(paginationInfo, dataSummary);

  let best = null;
  const consider = (value) => {
    const n = readNonNegativeNumber(value);
    if (n == null) return;
    best = best == null ? n : Math.max(best, n);
  };

  [
    paginationInfo.total_count,
    paginationInfo.totalCount,
    paginationInfo.total,
    paginationInfo.total_rows,
    paginationInfo.totalRows,
    dataSummary.total_rows,
    dataSummary.totalRows,
    dataSummary.total_row_count,
    dataSummary.totalRowCount,
  ].forEach(consider);

  const rowCount = readNonNegativeNumber(
    dataSummary.row_count ?? dataSummary.rowCount
  );
  if (rowCount != null) {
    if (rowCount > loaded) {
      consider(rowCount);
    } else if (hasMore === false) {
      consider(rowCount);
    }
  }

  const full = readNonNegativeNumber(fullResultCount);
  if (full != null && full > loaded) {
    consider(full);
  }

  const fallbackNum = readNonNegativeNumber(fallback);
  if (
    fallbackNum != null &&
    !(hasMore === true && fallbackNum <= loaded) &&
    (best == null || fallbackNum > best)
  ) {
    consider(fallbackNum);
  }

  return best != null ? Math.max(best, loaded) : null;
}

/** Shared footer totals for inline table, admin view, and full-view modal. */
export function resolveTableFooterState({
  rawResults = [],
  dataSummary = {},
  paginationInfo = {},
  storedTotalCount = null,
  rawResultsBuffer = null,
}) {
  const rows = Array.isArray(rawResults) ? rawResults : [];
  const loadedCount = rows.length;
  const bufferLen = Array.isArray(rawResultsBuffer) ? rawResultsBuffer.length : 0;
  const totalCount = resolveTotalRowCount(
    dataSummary,
    paginationInfo,
    storedTotalCount,
    loadedCount,
    bufferLen > loadedCount ? bufferLen : null
  );
  const hasMore = deriveTableHasMoreVisible({
    displayedCount: loadedCount,
    totalCount,
    bufferLength: bufferLen,
    paginationInfo,
    dataSummary,
  });
  const loadMoreCount = getLoadMoreBatchSize(loadedCount, totalCount);
  return { loadedCount, totalCount, hasMore, loadMoreCount };
}

export function parseTableMessageIndex(tableKey) {
  if (tableKey == null || tableKey === "") return NaN;
  const n = Number(String(tableKey).replace(/^table-/, ""));
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Rows to request on the next "Load more" click (caps at remaining total).
 */
export function getLoadMoreBatchSize(loadedCount, totalCount, batchSize = TABLE_PAGE_SIZE) {
  const loaded = Number(loadedCount) || 0;
  const batch = Number(batchSize) || TABLE_PAGE_SIZE;
  const total = readNonNegativeNumber(totalCount);

  if (total != null && total > loaded) {
    return Math.min(batch, total - loaded);
  }

  return batch;
}

/**
 * Rows to request on the first execute-sql call after applying sort (offset 0).
 * Without a user row limit: always TABLE_PAGE_SIZE (100).
 * With a user row limit: same value as wrapped SQL LIMIT (capped by total and API max).
 */
export function resolveSortExecuteLimit(userLimit, totalCount, loadedCount = 0) {
  const loaded = Number(loadedCount) || 0;
  let tableRows = readPositiveInt(totalCount);

  if (tableRows == null && loaded > 0) {
    tableRows = loaded;
  }

  const userRowLimit = readPositiveInt(userLimit);

  if (userRowLimit != null) {
    const capped =
      tableRows != null ? Math.min(userRowLimit, tableRows) : userRowLimit;
    return Math.min(Math.max(1, capped), EXECUTE_SQL_MAX_LIMIT);
  }

  return TABLE_PAGE_SIZE;
}

/** Append paginated SQL rows in order (do not dedupe — pages may repeat dimension values). */
export function mergeSqlResultPages(existing, incoming) {
  const base = Array.isArray(existing) ? existing : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (next.length === 0) return base;
  if (base.length === 0) return next;
  return [...base, ...next];
}

export function resolveTableLoadOffset(paginationEntry, existingRowCount) {
  const rowCount = Number(existingRowCount) || 0;
  const storedOffset = Number(paginationEntry?.offset);
  const storedLoaded = Number(paginationEntry?.loadedCount);

  return Math.max(
    rowCount,
    Number.isFinite(storedOffset) ? storedOffset : 0,
    Number.isFinite(storedLoaded) ? storedLoaded : 0
  );
}

export function mergeTableRowsWithProgress(existing, incoming, requestOffset) {
  const base = Array.isArray(existing) ? existing : [];
  const next = normalizeTableRows(
    Array.isArray(incoming) ? incoming : [],
    base.length > 0 ? base : null
  );
  const merged = mergeSqlResultPages(base, next);
  const appendedCount = next.length;

  return {
    merged,
    nextOffset: merged.length,
    appendedCount,
    dedupedAll: false,
  };
}

export function resolveNextTableOffset(paginationInfo, computedOffset) {
  const candidates = [
    paginationInfo?.next_offset,
    paginationInfo?.nextOffset,
  ];
  for (const value of candidates) {
    const n = readNonNegativeNumber(value);
    if (n != null) return n;
  }
  return computedOffset;
}

export function deriveTableHasMoreAfterLoad({
  mergedLength,
  totalCount,
  paginationInfo = {},
  newDataLength,
  limit,
  dedupedAll,
}) {
  const total = readNonNegativeNumber(totalCount);
  if (total != null) {
    return mergedLength < total;
  }

  const hasMoreFlag = pickPaginationHasMore(paginationInfo);
  if (hasMoreFlag === false) return false;
  if (hasMoreFlag === true) return true;

  if (dedupedAll && newDataLength > 0) return true;

  return newDataLength >= limit;
}

export function deriveTableHasMoreVisible({
  displayedCount,
  totalCount,
  bufferLength = 0,
  paginationInfo = {},
  dataSummary = {},
}) {
  const displayed = Number(displayedCount) || 0;
  const total = readNonNegativeNumber(totalCount);
  const buffer = Number(bufferLength) || 0;

  if (buffer > displayed) return true;

  // Known total is authoritative once in-memory buffer is exhausted
  if (total != null) {
    return displayed < total;
  }

  const hasMoreFlag = pickPaginationHasMore(paginationInfo, dataSummary);
  if (hasMoreFlag === true) return true;
  if (hasMoreFlag === false) return false;

  // Full first page with no explicit end — assume more rows may exist
  if (displayed >= TABLE_PAGE_SIZE) return true;

  return false;
}

export function processLoadMoreResponse({
  existingRows,
  newRows,
  requestOffset,
  limit,
  paginationInfo = {},
  dataSummary = {},
  previousTotalCount,
  bufferLength = 0,
}) {
  const { merged, nextOffset: computedOffset, appendedCount, dedupedAll } =
    mergeTableRowsWithProgress(existingRows, newRows, requestOffset);

  const bufferLen = Number(bufferLength) || 0;
  const updatedTotalCount = resolveTotalRowCount(
    dataSummary,
    paginationInfo,
    previousTotalCount,
    merged.length,
    bufferLen > merged.length ? bufferLen : null
  );

  const nextOffset = resolveNextTableOffset(paginationInfo, computedOffset);

  const hasMore = deriveTableHasMoreVisible({
    displayedCount: merged.length,
    totalCount: updatedTotalCount,
    bufferLength: bufferLen,
    paginationInfo,
    dataSummary,
  });

  return {
    merged,
    nextOffset,
    loadedCount: merged.length,
    totalCount: updatedTotalCount,
    hasMore,
    appendedCount,
  };
}

export function takeBufferedTableRows(buffer, startIndex, limit = TABLE_PAGE_SIZE) {
  if (!Array.isArray(buffer) || buffer.length === 0) {
    return { rows: [], nextIndex: 0, exhausted: true };
  }
  const start = Math.max(0, Number(startIndex) || 0);
  const batch = Math.max(1, Number(limit) || TABLE_PAGE_SIZE);
  const rows = buffer.slice(start, start + batch);
  const nextIndex = start + rows.length;
  return {
    rows,
    nextIndex,
    exhausted: nextIndex >= buffer.length,
  };
}

export function hasBufferedTableRows(buffer, loadedCount) {
  return Array.isArray(buffer) && buffer.length > (Number(loadedCount) || 0);
}

export function applyBufferedTableLoadMore({
  existingRows,
  rawResultsBuffer,
  limit,
  previousTotalCount,
  paginationInfo = {},
}) {
  if (!hasBufferedTableRows(rawResultsBuffer, existingRows.length)) return null;

  const batch = takeBufferedTableRows(rawResultsBuffer, existingRows.length, limit);
  if (batch.rows.length === 0) return null;

  const result = processLoadMoreResponse({
    existingRows,
    newRows: batch.rows,
    requestOffset: existingRows.length,
    limit,
    paginationInfo: batch.exhausted ? paginationInfo : { has_more: true },
    dataSummary: {},
    previousTotalCount,
  });

  result.hasMore = deriveTableHasMoreVisible({
    displayedCount: result.merged.length,
    totalCount: result.totalCount ?? previousTotalCount,
    bufferLength: rawResultsBuffer.length,
    paginationInfo: batch.exhausted ? paginationInfo : { has_more: true },
    dataSummary: {},
  });

  return { ...result, bufferExhausted: batch.exhausted };
}

/**
 * Normalize table display + pagination from a query API payload or history message.
 */
export function buildTableMetaFromSource({
  rawResults,
  pagination = {},
  dataSummary = {},
}) {
  const allResults = normalizeTableRows(
    Array.isArray(rawResults) ? rawResults : [],
    null
  );
  const displayedResults =
    allResults.length > TABLE_PAGE_SIZE
      ? allResults.slice(0, TABLE_PAGE_SIZE)
      : allResults;
  const displayedCount = displayedResults.length;
  const bufferLength = allResults.length;

  const totalCount = resolveTotalRowCount(
    dataSummary,
    pagination,
    null,
    displayedCount,
    bufferLength
  );

  const hasMore = deriveTableHasMoreVisible({
    displayedCount,
    totalCount,
    bufferLength,
    paginationInfo: pagination,
    dataSummary,
  });

  return {
    displayedResults,
    rawResultsBuffer: bufferLength > displayedCount ? allResults : null,
    totalCount,
    offset: displayedCount,
    loadedCount: displayedCount,
    hasMore,
    pagination,
    dataSummary,
  };
}

export function buildTablePaginationEntry(meta) {
  return {
    offset: meta.offset ?? meta.loadedCount ?? meta.displayedResults?.length ?? 0,
    loadedCount: meta.loadedCount ?? meta.displayedResults?.length ?? 0,
    totalCount: meta.totalCount ?? null,
    hasMore: meta.hasMore ?? false,
  };
}

export function buildInitialTablePagination(messages) {
  const initialPaginationState = {};
  (messages || []).forEach((msg, index) => {
    if (!msg?.rawResults?.length) return;
    const tableKey = `table-${index}`;
    const meta = buildTableMetaFromSource({
      rawResults: msg.rawResultsBuffer ?? msg.rawResults,
      pagination: msg.pagination ?? {},
      dataSummary: msg.dataSummary ?? {},
    });

    // History may already have partial display; prefer stored rows as displayed
    const displayedCount = msg.rawResults.length;
    const bufferLen = msg.rawResultsBuffer?.length ?? 0;
    const totalCount = resolveTotalRowCount(
      msg.dataSummary ?? {},
      msg.pagination ?? {},
      msg.totalCount,
      displayedCount,
      bufferLen
    );

    initialPaginationState[tableKey] = {
      offset: displayedCount,
      loadedCount: displayedCount,
      totalCount,
      hasMore: deriveTableHasMoreVisible({
        displayedCount,
        totalCount,
        bufferLength: msg.rawResultsBuffer?.length ?? 0,
        paginationInfo: msg.pagination ?? {},
        dataSummary: msg.dataSummary ?? {},
      }),
    };
  });
  return initialPaginationState;
}
